/**
 * Tamagui Configuration - Simulacra Theme
 * Using @tamagui/config/v3 as base (matches kitchen-sink)
 * Design Vision: Matrix-inspired cyber aesthetic meets Bloomberg Terminal
 *
 * "Simulacra" - Neo Finance theme blending digital reality with financial markets
 */
import { createTamagui, createTokens, createFont } from 'tamagui';
import { config as defaultConfig } from '@tamagui/config/v3';

// Custom color palette (used in themes)
// Blends Neo Matrix aesthetic with Bloomberg Terminal professionalism using elegant pastels
const colors = {
  // Backgrounds - Deep dark with subtle warmth (Bloomberg inspired)
  bgBase: '#0a0e14',           // Deep charcoal base
  bgElevated: '#151a23',       // Slightly elevated dark blue-gray
  bgTerminal: '#0d1218',       // Terminal dark with subtle blue tint
  bgGlass: 'rgba(21, 26, 35, 0.85)',  // Glassmorphism overlay

  // Neo Matrix accents - Refined green palette
  matrixPrimary: '#00ff88',    // Softer, more cyan-tinted Matrix green
  matrixDim: '#00d170',        // Medium brightness for hover states
  matrixGlow: '#00ff8840',     // Subtle glow effect
  matrixMuted: '#3d8b6a',      // Muted pastel green for backgrounds

  // Pastel finance indicators - Elegant and professional
  pastelGreen: '#7dd3c0',      // Soft teal-green for positive trends
  pastelRose: '#f4a6b8',       // Gentle rose for important alerts (replacing harsh red)
  pastelBlue: '#a8d5e2',       // Sky blue for information
  pastelAmber: '#ffd89b',      // Warm amber (Bloomberg nod) for highlights
  pastelLavender: '#c5b9d4',   // Dull lavender for secondary data
  pastelMint: '#b5ead7',       // Mint for success states

  // Text colors - High contrast with pastel accents
  textPrimary: '#e8eef2',      // Crisp white with slight blue tint
  textSecondary: '#9ba5b0',    // Medium gray-blue
  textTerminal: '#00ff88',     // Matches Matrix primary
  textAccent: '#7dd3c0',       // Pastel teal for links/accents
  textMuted: '#6b7785',        // Subtle gray for metadata

  // Border colors - Subtle depth with pastel highlights
  borderSubtle: '#1f2933',     // Very subtle border
  borderDefault: '#2d3748',    // Default border with warmth
  borderStrong: '#4a5568',     // Strong borders
  borderNeon: '#00ff88',       // Neo accent border
  borderPastel: '#7dd3c0',     // Soft pastel border for cards
};

// Custom tokens for Neo Finance design system
const tokens = createTokens({
  ...defaultConfig.tokens,

  // Spacing tokens
  space: {
    ...defaultConfig.tokens.space,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 24,
    6: 32,
    7: 48,
    8: 64,
  },

  // Size tokens
  size: {
    ...defaultConfig.tokens.size,
    sm: 32,
    md: 40,
    lg: 48,
    xl: 56,
    xxl: 64,
  },

  // Radius tokens
  radius: {
    ...defaultConfig.tokens.radius,
    0: 0,
    1: 2,
    2: 4,
    3: 8,
    4: 12,
    5: 16,
  },

  // Color tokens (reference custom colors)
  color: colors,
});

// Custom fonts for Neo Finance aesthetic
const headingFont = createFont({
  family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  size: {
    1: 11,
    2: 13,
    3: 15,
    4: 18,
    5: 24,
    6: 32,
    7: 48,
  },
  lineHeight: {
    1: 16,
    2: 20,
    3: 24,
    4: 28,
    5: 32,
    6: 40,
    7: 56,
  },
  weight: {
    1: '400',
    2: '500',
    3: '600',
    4: '700',
  },
  letterSpacing: {
    1: 0,
    2: -0.5,
    3: -1,
  },
});

const bodyFont = createFont({
  family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  size: {
    1: 11,
    2: 13,
    3: 15,
    4: 18,
    5: 24,
    6: 32,
  },
  lineHeight: {
    1: 16,
    2: 20,
    3: 24,
    4: 28,
    5: 32,
    6: 40,
  },
  weight: {
    1: '400',
    2: '500',
    3: '600',
  },
});

const monoFont = createFont({
  family: 'JetBrains Mono, Fira Code, Consolas, Monaco, monospace',
  size: {
    1: 11,
    2: 13,
    3: 15,
    4: 18,
  },
  lineHeight: {
    1: 16,
    2: 20,
    3: 24,
    4: 28,
  },
  weight: {
    1: '400',
    2: '500',
    3: '600',
  },
});

// Simulacra Dark Theme - Neo Finance with elegant pastels
const simulacraDark = {
  // Neo Matrix green - refined and sophisticated
  green10: tokens.color.matrixPrimary,  // #00ff88 - Softer cyan-tinted green
  green9: tokens.color.matrixDim,       // #00d170 - Medium brightness
  green8: tokens.color.matrixGlow,      // #00ff8840 - Subtle glow
  green7: tokens.color.matrixMuted,     // #3d8b6a - Muted pastel green

  // Backgrounds - Bloomberg-inspired depth
  background: tokens.color.bgBase,           // #0a0e14 - Deep charcoal
  backgroundHover: tokens.color.bgElevated,  // #151a23 - Elevated dark blue-gray
  backgroundPress: tokens.color.bgTerminal,  // #0d1218 - Terminal dark
  backgroundFocus: tokens.color.bgElevated,

  // Pastel finance indicators - Elegant alternatives
  red10: tokens.color.pastelRose,       // #f4a6b8 - Gentle rose
  red9: '#e27396',                      // Slightly deeper rose for hover
  blue10: tokens.color.pastelBlue,      // #a8d5e2 - Sky blue
  yellow10: tokens.color.pastelAmber,   // #ffd89b - Warm amber (Bloomberg nod)
  purple10: tokens.color.pastelLavender, // #c5b9d4 - Dull lavender
  cyan10: tokens.color.pastelGreen,      // #7dd3c0 - Soft teal-green

  // Text colors - High contrast with pastel accents
  color: tokens.color.textPrimary,        // #e8eef2 - Crisp white
  colorHover: tokens.color.textAccent,    // #7dd3c0 - Pastel teal
  colorPress: tokens.color.textSecondary, // #9ba5b0 - Medium gray-blue
  colorFocus: tokens.color.matrixPrimary, // #00ff88 - Neo green

  // Borders - Subtle with pastel highlights
  borderColor: tokens.color.borderDefault,       // #2d3748
  borderColorHover: tokens.color.borderPastel,   // #7dd3c0
  borderColorFocus: tokens.color.matrixPrimary,  // #00ff88
  borderColorPress: tokens.color.borderStrong,   // #4a5568

  // Placeholder
  placeholderColor: tokens.color.textMuted,   // #6b7785
};

// Simulacra Light Theme - Day Trader Mode with soft pastels
const simulacraLight = {
  // Neo Matrix green - adapted for light mode
  green10: '#00b36b',         // Darker green for contrast
  green9: '#00d170',          // Medium green
  green8: '#7dd3c0',          // Soft teal-green
  green7: '#b5ead7',          // Very light mint

  // Backgrounds - Clean light with subtle warmth
  background: '#f8fafb',          // Off-white with cool tint
  backgroundHover: '#ecf1f4',     // Light gray-blue hover
  backgroundPress: '#e0e7eb',     // Pressed state
  backgroundFocus: '#ecf1f4',

  // Pastel finance indicators - Vibrant but professional
  red10: '#e27396',           // Medium rose (same as dark theme for consistency)
  red9: '#d55d7f',            // Deeper rose for hover
  blue10: '#5ba3c5',          // Medium sky blue
  yellow10: '#f0b86e',        // Warm amber
  purple10: '#9b8ab8',        // Medium lavender
  cyan10: '#4fb3a0',          // Medium teal

  // Text colors - Dark text on light background
  color: '#1a2332',               // Almost black with blue tint
  colorHover: '#2d4b66',          // Dark blue-gray
  colorPress: '#4a5f7a',          // Medium blue-gray
  colorFocus: '#00b36b',          // Darker Neo green

  // Borders - Subtle definition
  borderColor: '#d1d9e0',         // Light gray border
  borderColorHover: '#7dd3c0',    // Pastel teal hover
  borderColorFocus: '#00b36b',    // Neo green focus
  borderColorPress: '#b0bcc9',    // Medium gray press

  // Placeholder
  placeholderColor: '#7a8a9e',    // Medium gray text
};

export const config = createTamagui({
  ...defaultConfig,
  tokens,
  themes: {
    ...defaultConfig.themes,
    // Simulacra Dark - Neo Finance with elegant pastels
    dark: {
      ...defaultConfig.themes.dark,
      ...simulacraDark,
    },
    // Simulacra Light - Day Trader Mode
    light: {
      ...defaultConfig.themes.light,
      ...simulacraLight,
    },
  },
  fonts: {
    ...defaultConfig.fonts,
    heading: headingFont,
    body: bodyFont,
    mono: monoFont,
  },
});

export type Conf = typeof config;

declare module 'tamagui' {
  interface TamaguiCustomConfig extends Conf {}
}

export default config;
